import type { SuperTableColumnKind, SuperTableRow } from "@glide/component-utils";
import type { DataPlotRowData, WireDataPlotDataPoint } from "@glide/fluent-components/dist/js/base-components";
import { DataPlotType } from "@glide/wire";
import type { NewTableProps } from "../../components/new-table/new-table";
import type { TableProps, DiscoveryChart } from "./wire-discovery-provider";
import isNumber from "lodash/isNumber";

export type SingleDataPointView = {
    label: string;
    value: any;
};

type DisplayOptionsFromJSONResult = {
    table: TableProps;
    chartSuggestions: DiscoveryChart[];
    singleDataPointView?: SingleDataPointView;
};

function formatNumber(value: number, format: "short" | "long" = "short") {
    return new Intl.NumberFormat(navigator.language, {
        notation: format === "short" ? "compact" : "standard",
        maximumFractionDigits: 1,
    }).format(value);
}

export function computeTableForJSONData(
    jsonArray: Record<string, unknown>[],
    allKeys = Array.from(new Set(jsonArray.flatMap(item => Object.keys(item))))
): TableProps {
    // Table: define columns
    const columns = allKeys.map(key => ({
        kind: "text" as SuperTableColumnKind,
        header: key.replace(/_/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2"), // Replace underscores and camelCase
        columnName: key,
        sizeOptions: {
            sizeKind: "auto",
        } as any,
        headerAction() {
            // console.log("Header action");
        },
    }));

    // Table: build rows
    const rows: SuperTableRow[] = jsonArray.map(item =>
        allKeys.map(key => {
            let value = item[key] !== undefined ? item[key] : "";
            if (isNumber(value)) {
                value = formatNumber(value, "short");
            }
            return {
                kind: "text" as SuperTableColumnKind,
                value,
            } as any;
        })
    );

    return {
        columns,
        rows,
        tableLabel: "_not_consumed",
        activeSort: undefined,
        appID: "mock-app",
        style: "minimal" as NewTableProps["style"],
    };
}

export function computeDisplaySuggestionsForJSONData(
    jsonArray: Record<string, unknown>[]
): DisplayOptionsFromJSONResult | undefined {
    if (jsonArray.length === 0) return undefined;

    const allKeys = Array.from(new Set(jsonArray.flatMap(item => Object.keys(item))));

    const table = computeTableForJSONData(jsonArray, allKeys);

    // Identify all numeric keys
    const numericKeys = allKeys.filter(key => jsonArray.some(item => typeof item[key] === "number"));
    const potentialXAxisKeys = allKeys.filter(key => !numericKeys.includes(key));

    // Build chart suggestions based on each potential X-axis
    const chartSuggestions = potentialXAxisKeys.map(xKey => {
        // TODO?: for each of these we might want to aggregate to make it work better...
        // Then again, we have LLM we should just iterate over the chart types instead
        const data: DataPlotRowData[] = jsonArray.map(
            (item, index) =>
                ({
                    name: (item[xKey] as string | undefined)?.toString() ?? `Row ${index + 1}`,
                    points: Object.fromEntries(
                        numericKeys.map(numKey => [
                            numKey,
                            {
                                value: typeof item[numKey] === "number" ? item[numKey] : 0,
                            },
                        ])
                    ),
                } as any)
        );

        const columnsToDisplay: WireDataPlotDataPoint[] = numericKeys.map(key => ({
            label: key.replace(/_/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2"),
            color: undefined,
            dataKey: key,
            type: DataPlotType.Bar,
            rollupKind: undefined,
        }));

        return {
            graphTitle: `By ${xKey}`,
            data,
            columnsToDisplay,
            showLegend: true,
            showYAxisLabels: true,
        };
    });

    let singleDataPointView: { label: string; value: any } | undefined = undefined;
    if (jsonArray.length === 1 && allKeys.length === 1) {
        const label = allKeys[0];
        singleDataPointView = {
            label,
            value: jsonArray[0][label],
        };
    }

    return {
        table,
        chartSuggestions: numericKeys.length > 0 ? chartSuggestions : [],
        singleDataPointView,
    };
}
